import React, { useState } from "react"
import { Box, Grid, Container } from "@mui/material"
import { makeStyles } from "makeStyles"
import ItemsCarousel from "react-items-carousel"
import Icon from "@mdi/react"
import { mdiChevronRight, mdiChevronLeft } from "@mdi/js"

import Link from "components/i18n/Link"
import SEO from "@secureo/common/components/SEO"
import BreadCrumbs, { BreadCrumb } from "components/BreadCrumbs"

import { helpCenterAncestorBreadCrumbs } from "./index"

import useWindowSize from "@secureo/common/hooks/useWindowSize"

import { CustomAppProps } from "pages/_app"

import { NavCategory } from "@secureo/common/utils/commerceTools/graphql/queries/fetchNavCategories"
import navCategories from "config/navCategories.json"

import ContactForm from "components/Forms/ContactForm"

// svg
import Filialketten from "svg/contentPages/filialen.svg"
import Hotels from "svg/contentPages/hotels.svg"
import Bauunternehmen from "svg/contentPages/bauunternehmen.svg"
import GewerbekundenTop from "svg/contentPages/gewerbekunden1.svg"
import GewerbekundenBottom from "svg/contentPages/gewerbekunden2.svg"

const useStyles = makeStyles()({
	centerImage: {
		height: 600,
		width: 600,
		marginTop: -200,
		marginBottom: -200,
		objectFit: "contain",
	},
	consultationDescription: {
		maxWidth: 300,
	},
	slide: {
		height: 140,
		background: "white",
		width: "95%",
		margin: "auto",
		borderRadius: 5,
		boxShadow: "0px 5px 5px 0px rgba(0,0,0,0.15)",
		position: "relative",
		zIndex: -1,
	},
	arrowButtonLeft: {
		padding: 5,
		background: "white",
		borderRadius: 5,
		border: "2px solid whitesmoke",
		boxShadow: "2px 2px 5px 0px rgba(0,0,0,0.15)",
		outline: "none",
		"&:hover": {
			background: "whitesmoke",
		},
	},
	arrowButtonRight: {
		padding: 5,
		background: "white",
		borderRadius: 5,
		border: "2px solid whitesmoke",
		boxShadow: "2px 2px 5px 0px rgba(0,0,0,0.15)",
		outline: "none",
		"&:hover": {
			background: "whitesmoke",
		},
	},
	slideTitle: {
		position: "absolute",
		bottom: 20,
		marginLeft: "auto",
		marginRight: "auto",
		left: 0,
		right: 0,
		textAlign: "center",
		color: "black",
	},
	"@media only screen and (max-width: 960px)": {
		centerImage: {
			height: 250,
			width: 250,
			marginTop: 0,
			marginBottom: 0,
			objectFit: "contain",
		},
		consultationDescription: {
			maxWidth: "100%",
			textAlign: "center",
		},
	},
	"@media only screen and (max-width: 600px)": {
		arrowButtonRight: {
			display: "none",
		},
		arrowButtonLeft: {
			display: "none",
		},
		slideTitle: {
			fontSize: "0.9rem",
		},
	},
})

const BusinessCustomersPage = ({ asPath, locale }: CustomAppProps & Props) => {
	const { classes } = useStyles()
	const [activeItemIndex, setActiveItemIndex] = useState(0)
	const { innerWidth } = useWindowSize()

	const chevronWidth = 0
	const isDesktop = innerWidth < 1280
	const isTablet = innerWidth < 600

	const slideCount = !isDesktop ? 3 : isTablet ? 2 : 2

	const currentPath: BreadCrumb = {
		href: "/help/business-customers",
		label: "Gewerbekunden",
	}

	return (
		<>
			<SEO asPath={asPath} appendLocalizedShopNameWithPipe title="Gewerbekunden" />
			<div style={{ position: "relative", overflow: "hidden" }}>
				<div
					style={{
						backgroundImage:
							"url(https://res.cloudinary.com/digitalzylinder-shop/image/upload/v1590561445/DigiShop2/Content/Half-Circle-Background.png)",
						height: 570,
						position: "absolute",
						marginTop: -100,
						left: 0,
						width: "100%",
						backgroundRepeat: "no-repeat",
						zIndex: -1,
					}}
				/>
				<Container>
					<div style={{ marginLeft: -25 }}>
						<BreadCrumbs
							locale={locale}
							currentPath={currentPath}
							ancestors={helpCenterAncestorBreadCrumbs}
						/>
					</div>
					<Box pt={5} mb={5} style={{ position: "relative" }}>
						<Grid container spacing={3}>
							<Grid item xs={12} md={6}>
								<div style={{ maxWidth: 450 }}>
									<div
										style={{
											color: "#4691CE",
											textTransform: "uppercase",
											fontSize: 13,
										}}
									>
										<strong>Kompetente Projektabwicklung</strong>
									</div>
									<Box mb={2}>
										<h1 style={{ margin: 0 }}>
											Gewerbekunden als Endverbraucher
										</h1>
									</Box>
									<p style={{ color: "#505e5f" }}>
										Durch unsere Erfahrung im Projektbereich bieten wir Ihnen
										maßgeschneiderte Lösungen für Ihr Projekt oder Unternehmen
										in Österreich, Deutschland und in der Schweiz.
									</p>
								</div>
							</Grid>
							<Grid item xs={12} md={6}>
								<GewerbekundenTop
									style={{
										width: "100%",
										height: 300,
										objectFit: "cover",
										borderRadius: 15,
									}}
								/>
							</Grid>
						</Grid>
						<Box py={5} my={10}>
							<Box mb={5} style={{ maxWidth: 450 }} className="h3">
								„Wir bieten eine kompetente Projektabwicklung für Großkunden wie zum
								Beispiel: … “
							</Box>
							<Grid container spacing={3}>
								<Grid item xs={12} md={4} style={{ textAlign: "center" }}>
									<Filialketten
										style={{
											width: "100%",
											maxWidth: "200px",
											height: 200,
											maxHeight: "100%",
										}}
									/>
									<div>
										<strong>Filialketten</strong>
									</div>
								</Grid>
								<Grid item xs={12} md={4} style={{ textAlign: "center" }}>
									<Hotels
										style={{
											width: "100%",
											maxWidth: "200px",
											height: 200,
											maxHeight: "100%",
										}}
									/>
									<div>
										<strong>Hotels</strong>
									</div>
								</Grid>
								<Grid item xs={12} md={4} style={{ textAlign: "center" }}>
									<Bauunternehmen
										style={{
											width: "100%",
											maxWidth: "200px",
											height: 200,
											maxHeight: "100%",
										}}
									/>
									<div>
										<strong>Bauunternehmen</strong>
									</div>
								</Grid>
								<Grid item xs={12}>
									<Box mt={5} className="h3">
										Ihre Vorteile bei uns
									</Box>
								</Grid>
								<Grid item xs={12} md={4}>
									<div
										style={{
											background: "#E7EFF6",
											padding: 10,
											textAlign: "center",
											borderRadius: 5,
										}}
									>
										<strong>
											<span style={{ color: "#4691CE" }}>✔</span> Intensive
											persönliche Beratung
										</strong>
									</div>
								</Grid>
								<Grid item xs={12} md={4}>
									<div
										style={{
											background: "#E7EFF6",
											padding: 10,
											textAlign: "center",
											borderRadius: 5,
										}}
									>
										<strong>
											<span style={{ color: "#4691CE" }}>✔</span>{" "}
											Verlässliche Ausführung des Projekts
										</strong>
									</div>
								</Grid>
								<Grid item xs={12} md={4}>
									<div
										style={{
											background: "#E7EFF6",
											padding: 10,
											textAlign: "center",
											borderRadius: 5,
										}}
									>
										<strong>
											<span style={{ color: "#4691CE" }}>✔</span> Günstiges
											Preis/Leistungverhältnis
										</strong>
									</div>
								</Grid>
							</Grid>
						</Box>
					</Box>
				</Container>
				<Box my={5} py={5} style={{ background: "#EDEFF0" }}>
					<Container>
						<Grid container spacing={3}>
							<Grid item xs={12} sm={5}>
								<div style={{ maxWidth: 400 }}>
									<Box mb={1} mt={5}>
										<h3>
											<strong>Leistungen</strong>
										</h3>
									</Box>
									<p>
										Neben der Austattung mit Briefkastenanlagen in einem
										komplettem System inkl. Montage und Schulung bieten wir die
										klassichen Leistungen eines Sicherheits-Fachbetriebs
									</p>
								</div>
							</Grid>
							<Grid item xs={12} sm={7}>
								<Box>
									<div style={{ padding: `0 ${chevronWidth}px` }}>
										<ItemsCarousel
											requestToChangeActive={setActiveItemIndex}
											activeItemIndex={activeItemIndex}
											numberOfCards={slideCount}
											gutter={10}
											leftChevron={
												<button className={classes.arrowButtonLeft}>
													<Icon
														path={mdiChevronLeft}
														color="black"
														size={1}
													/>
												</button>
											}
											rightChevron={
												<button className={classes.arrowButtonRight}>
													<Icon
														path={mdiChevronRight}
														color="black"
														size={1}
													/>
												</button>
											}
											outsideChevron
											chevronWidth={chevronWidth}
											infiniteLoop={false}
											// showSlither={true}
										>
											{navCategories.map((navCategory) => {
												const { name, slug, categoryProductImage } =
													navCategory as any

												const image =
													categoryProductImage ??
													"https://via.placeholder.com/100x100"

												return (
													<Link href={`/c/${slug}`} key={slug}>
														<Box
															style={{
																height: "auto",
																outline: 0,
																textAlign: "center",
																marginBottom: 10,
																background: "transparent",
															}}
														>
															<img
																alt={name}
																src={image}
																style={{
																	height: 120,
																	width: 120,
																	margin: "auto",
																	marginBottom: -80,
																	objectFit: "contain",
																	zIndex: 1,
																	mixBlendMode: "multiply",
																}}
															/>
															<div
																className={classes.slide}
																style={{ textAlign: "center" }}
															>
																<Box
																	px={1}
																	className={classes.slideTitle}
																>
																	{name}
																</Box>
															</div>
														</Box>
													</Link>
												)
											})}
										</ItemsCarousel>
									</div>
								</Box>
							</Grid>
						</Grid>
					</Container>
				</Box>
				<Container>
					<Box my={5} py={5} style={{ position: "relative" }}>
						<Grid container spacing={3}>
							<Grid item xs={12} md={6} style={{ textAlign: "center" }}>
								<GewerbekundenBottom
									style={{
										boxShadow: "2px 2px 10px 0px rgba(0,0,0,0.15)",
										margin: "auto",
										marginTop: 30,
									}}
								/>
							</Grid>
							<Grid item xs={12} md={6}>
								<Box p={3}>
									<Box mb={2} className="h2" style={{ fontSize: 26 }}>
										<strong>Wir freuen uns auf Ihre Anfrage!</strong>
									</Box>
									<ContactForm />
								</Box>
							</Grid>
						</Grid>
					</Box>
				</Container>
			</div>
		</>
	)
}

interface Props {
	navCategories: NavCategory[]
}

export default BusinessCustomersPage
